import {
  AdminMembersReportPageParams,
  AdminMembersReportPageStartDate,
} from "@/admin/insights/member-reports/AdminMembersReportPage"
import { MemberReportDailyDiscussionsChart } from "@/admin/insights/member-reports/tabs/AdminMembersReportDailyDiscussionsSection"
import {
  AdminMembersReportDailyDiscussionsChartQuery,
  ReportingUserActivityEntity,
  ReportingUserActivityKind,
} from "@/admin/insights/member-reports/tabs/__generated__/AdminMembersReportDailyDiscussionsChartQuery.graphql"
import { useLabels } from "@/core/context/LabelsContext"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoChartTooltipContent,
  DiscoEmptyState,
  DiscoLineChart,
  DiscoSection,
  DiscoSpinner,
  DiscoText,
} from "@disco-ui"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import { DATE_FORMAT } from "@utils/time/timeConstants"
import { formatDateWithOptions } from "@utils/time/timeUtils"
import { ParentSize } from "@visx/responsive"
import React from "react"
import { graphql } from "react-relay"

type TotalActivityOnDay = {
  date: string
  totalActivity: number
}

interface Props {
  chart: MemberReportDailyDiscussionsChart
  filters: {
    startDate: AdminMembersReportPageStartDate
  }
}

const AdminMembersReportDailyDiscussionsChart = ({ chart, filters }: Props) => {
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const classes = useStyles()
  const labels = useLabels()

  const [{ node }] =
    Relay.useRefetchableQuery<AdminMembersReportDailyDiscussionsChartQuery>(
      graphql`
        query AdminMembersReportDailyDiscussionsChartQuery(
          $id: ID!
          $startDate: String
          $kinds: [ReportingUserActivityKind!]
          $entities: [ReportingUserActivityEntity!]
        ) {
          node(id: $id) {
            __typename
            ... on OrganizationMembership {
              id
              __typename
              dailyUserActivityReport(
                startDate: $startDate
                kinds: $kinds
                entities: $entities
              ) {
                data {
                  date
                  totalActivity
                }
              }
            }
          }
        }
      `,
      {
        id: queryParams.om!,
        startDate: filters.startDate,
        kinds: getKinds(),
        entities: getEntities(),
      },
      {
        fetchPolicy: "network-only",
      }
    )

  const organizationMembership = Relay.narrowNodeType(node, "OrganizationMembership")
  if (!organizationMembership) return null

  const { data } = organizationMembership.dailyUserActivityReport as {
    data: TotalActivityOnDay[] | null
  }

  return data?.length ? (
    <ParentSize className={classes.graphSvg}>
      {({ width, height }) => (
        <DiscoLineChart
          width={width}
          height={height}
          data={data}
          xAccessor={(d) => d.date}
          yAccessor={(d) => d.totalActivity}
          renderTooltip={({ tooltipData }, xAccessor) => {
            if (!tooltipData?.nearestDatum?.datum) return
            const x = xAccessor(tooltipData.nearestDatum.datum)

            const dateData = data.find((d) => d.date === x)
            if (!dateData) return
            return (
              <DiscoChartTooltipContent>
                <DiscoText color={"common.white"} variant={"body-sm-600"}>
                  {formatDateWithOptions({
                    format: DATE_FORMAT.DEFAULT,
                  })(new Date(x))}
                </DiscoText>
                <DiscoText
                  color={"common.white"}
                  variant={"body-sm-600"}
                >{`- Total Activity: ${dateData.totalActivity}`}</DiscoText>
              </DiscoChartTooltipContent>
            )
          }}
        />
      )}
    </ParentSize>
  ) : (
    <DiscoEmptyState
      testid={"AdminMembersReportDailyDiscussionsChart.EmptyState"}
      subtitle={`${labels.admin_member.singular} has no activity yet.`}
    />
  )

  function getKinds(): ReportingUserActivityKind[] | undefined {
    switch (chart) {
      case "messages":
        return ["channel_message_sent"]
      case "comments":
        return ["commented"]
      default:
        throw new Error("DEV ERROR: Unhandled chart type")
    }
  }

  function getEntities(): ReportingUserActivityEntity[] | undefined {
    switch (chart) {
      case "messages":
        return ["ChatChannel"]
      case "comments":
        return [
          "CurriculumItem",
          "Assignment",
          "AssignmentSubmission",
          "Lesson",
          "Task",
          "Post",
        ]
      default:
        throw new Error("DEV ERROR: Unhandled chart type")
    }
  }
}

export const AdminMembersReportDailyDiscussionsChartSkeleton = () => {
  const classes = useStyles()
  return (
    <DiscoSection className={classes.root} padding={3}>
      <DiscoSpinner absoluteCenter />
    </DiscoSection>
  )
}

const useStyles = makeUseStyles((theme) => ({
  root: {
    position: "relative",
    height: 400,
  },
  graphSvg: {
    paddingBottom: theme.spacing(9),
  },
}))

export default Relay.withSkeleton({
  component: AdminMembersReportDailyDiscussionsChart,
  skeleton: () => <DiscoSpinner />,
})
